import { useState, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { showErrorToast, showSuccessToast, getErrorMessage } from '@/lib/toast-utils';

interface UserInfo {
  firstName: string;
  lastName: string;
  address: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  email: string;
  phone: string;
  image: string;
  userName: string;
}

let cachedUserInfo: UserInfo | null = null;

export function clearUserInfoCache() {
  cachedUserInfo = null;
}

export function useUserInfo() {
  const [userInfo, setUserInfo] = useState<UserInfo | null>(cachedUserInfo);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { makeAuthenticatedCall } = useAuthenticatedApi();

  const fetchUserInfo = useCallback(async (force = false) => {
    if (cachedUserInfo && !force) {
      setUserInfo(cachedUserInfo);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await makeAuthenticatedCall<UserInfo>('get-user-info');

      if (apiError) {
        throw new Error(apiError);
      }

      cachedUserInfo = data || null;
      setUserInfo(cachedUserInfo);
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to fetch user info');
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [makeAuthenticatedCall]);

  const updateUserInfo = async (updatedData: Partial<UserInfo>) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data, error: apiError } = await makeAuthenticatedCall<UserInfo>('get-user-info', {
        userData: updatedData,
        action: 'update'
      });

      if (apiError) {
        throw new Error(apiError);
      }

      setUserInfo(data || null);
      showSuccessToast("Contact information updated successfully");
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to update user info');
      setError(errorMessage);
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    userInfo,
    isLoading,
    error,
    fetchUserInfo,
    updateUserInfo,
  };
}
